import {
  EditOutlined,
  FiberManualRecord as FiberManualRecordIcon,
  InfoOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { VenueDateProperties } from '@/domain/project-venue-dates/entities/project-venue-dates.entity.ts';
import { VenuePreset } from '@/domain/project-venue-presets/entities/project-venue-presets.entity.ts';
import { selectProjectVenuesPresetsItems } from '@/domain/project-venue-presets/project-venue-presets.selectors.ts';
import { FSCard } from '@/ui/components/FSCard.tsx';
import { FSTextField } from '@/ui/components/input/FSTextField.tsx';
import { useAppSelector } from '@/ui/hook/store.ts';
import { translate } from '@/ui/i18n';
import { ModalPresetJaugeValid } from '@/ui/pages/app/project/venue/dates/components/modal-preset-jauge-valid/ModalPresetJaugeValid';
import { AverageCapacityForm } from '@/ui/pages/app/project/venue/default-datas/components/forms/AverageCapacityForm.tsx';
import { ColorsRaw } from '@/ui/theme/colors-raw.ts';

export function VenueDatePreset({
  selectedDatePreset,
  onChangePreset,
  readonly = false,
  onClickPresets,
}: {
  selectedDatePreset?: VenueDateProperties;
  onChangePreset: (datePreset: VenueDateProperties) => void;
  readonly: boolean;
  onClickPresets?: () => void;
}) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingAverageCapacity, setEditingAverageCapacity] =
    React.useState<boolean>(false);

  const presets = useAppSelector(selectProjectVenuesPresetsItems);

  const selectedPreset = presets.find(
    (preset) => preset.id === selectedDatePreset?.presetId,
  );

  const presetOptions = [];

  const finalAverageCapacity = selectedDatePreset?.customAvgCapacity
    ? selectedDatePreset?.avgCapacity
    : selectedPreset?.avgCapacity || 0;

  const handleSelect = (presetId: number) => {
    const presetFound = presets.find((preset) => preset.id === presetId);
    if (presetFound) {
      onChangePreset({
        presetId: presetFound.id,
        avgCapacity: finalAverageCapacity,
        customAvgCapacity: selectedDatePreset?.customAvgCapacity,
      });
    }
  };

  const handleChangeDateAverageCapacity = (preset: VenuePreset) => {
    onChangePreset({
      presetId: preset.id,
      avgCapacity: preset.avgCapacity,
      customAvgCapacity: preset.customAvgCapacity,
    });
    setEditingAverageCapacity(false);
  };

  for (const preset of presets) {
    presetOptions.push(
      <MenuItem key={preset.id} value={preset.id}>
        {preset.presetColor && (
          <ListItemIcon sx={{ minWidth: 28 }}>
            <FiberManualRecordIcon
              sx={{ color: preset.presetColor, fontSize: 16 }}
            />
          </ListItemIcon>
        )}
        {preset.presetTitle}
      </MenuItem>,
    );
  }

  if (onClickPresets) {
    presetOptions.push(<Divider />);
    presetOptions.push(
      <MenuItem
        key="change-presets"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClickPresets();
        }}
      >
        <ListItemIcon>
          <SettingsOutlined sx={{ fontSize: 16 }} />
        </ListItemIcon>
        {translate('VenueDates.QuizPanel.change-presets')}
      </MenuItem>,
    );
  }

  return (
    <Box padding={3}>
      <ModalPresetJaugeValid
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onValid={() => setEditingAverageCapacity(true)}
      />
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography fontSize={24} fontWeight={800}>
          {translate('VenueDates.QuizPanel.default-data')}
        </Typography>
      </Stack>
      <Typography mt={2}>
        {translate('VenueDates.QuizPanel.datas-apply-by-default')}
      </Typography>
      <FSTextField
        select
        value={selectedDatePreset?.presetId ?? ''}
        sx={{ width: '100%', mt: 2 }}
        onChange={(event) => handleSelect(+event.target.value)}
      >
        {presetOptions}
      </FSTextField>
      {selectedPreset && (
        <>
          {editingAverageCapacity ? (
            <AverageCapacityForm
              onEdit={handleChangeDateAverageCapacity}
              venueDefaultDatas={{
                ...selectedPreset,
                avgCapacity: finalAverageCapacity,
                customAvgCapacity: true,
              }}
              forceEdit
              onClose={() => setEditingAverageCapacity(false)}
            />
          ) : (
            <Box sx={{ mt: 2 }}>
              <FSCard variant="elevation" sx={{ p: 2 }}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Typography fontSize={20} fontWeight={'bold'}>
                    {translate('VenueDates.QuizPanel.public_jauge')}
                  </Typography>
                  {!readonly && (
                    <Button
                      sx={{ minWidth: 'fit-content', p: 1 }}
                      onClick={() => setModalOpen(true)}
                    >
                      <EditOutlined sx={{ color: ColorsRaw.basic.Cream }} />
                    </Button>
                  )}
                </Stack>
                <Stack>
                  {selectedDatePreset?.customAvgCapacity && (
                    <CustomAvgCapacityMessage />
                  )}
                  <AnswerTag mt={1}>
                    {`${finalAverageCapacity} ${translate(finalAverageCapacity && finalAverageCapacity > 1 ? 'ImpactQuestion.ACCOMMODATION_PUBLIC.people' : 'ImpactQuestion.ACCOMMODATION_PUBLIC.person')}`}
                  </AnswerTag>
                </Stack>
              </FSCard>
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <FSCard variant="elevation" sx={{ p: 2 }}>
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography fontSize={20} fontWeight={'bold'}>
                  {translate('VenueDates.QuizPanel.public_preset')}
                </Typography>
              </Stack>

              <Stack>
                <Typography mt={2} fontSize={14}>
                  {translate('VenueDates.QuizPanel.transportation-mode')}
                </Typography>
                {selectedPreset.mobility?.map((mobility) => (
                  <AnswerTag key={mobility.type} mt={1}>
                    {translate(
                      `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.TransportationModeValues.${mobility.type}`,
                    )}
                    {`: ${mobility.percentage}% - ${mobility.distance}km`}
                  </AnswerTag>
                ))}

                <Typography mt={2} fontSize={14}>
                  {translate('VenueDates.QuizPanel.accommodation')}
                </Typography>
                {selectedPreset.accommodation?.map((accommodation) => (
                  <AnswerTag key={accommodation.type} mt={1}>
                    {translate(
                      `ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.AccommodationTypeValues.${accommodation.type}`,
                    )}
                    {`: ${accommodation.percentage}%`}
                  </AnswerTag>
                ))}
              </Stack>
            </FSCard>
          </Box>
          <Box sx={{ mt: 2 }}>
            <FSCard
              variant="elevation"
              sx={{ p: 2, mt: 2, borderRadius: '8px' }}
            >
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography fontSize={20} fontWeight={'bold'}>
                  {translate('VenueDates.QuizPanel.technique')}
                </Typography>
              </Stack>
              <Stack>
                {selectedPreset.equipment?.map((equipment) => (
                  <AnswerTag key={equipment.type} mt={1}>
                    {`${equipment.quantity} 
                  ${translate(
                    `ProjectsVenue.DefaultDatas.Public.Forms.Equipment.EquipmentTypeValues.${equipment.type}`,
                  ).toLowerCase()}`}
                  </AnswerTag>
                ))}
              </Stack>
            </FSCard>
          </Box>
        </>
      )}
    </Box>
  );
}

const AnswerTag = ({
  children,
  mt,
}: {
  children: React.ReactNode;
  mt?: string | number;
}) => (
  <Box
    sx={{
      bgcolor: ColorsRaw.anthracite.Anthracite_4,
      px: 1,
      py: 0.5,
      borderRadius: '8px',
      width: 'fit-content',
    }}
    mt={mt}
  >
    <Typography fontSize={16} fontWeight={700}>
      {children}
    </Typography>
  </Box>
);

const CustomAvgCapacityMessage = () => (
  <Stack direction="row" alignItems="center" gap={0.5}>
    <InfoOutlined sx={{ color: 'text.secondary', fontSize: 14 }} />
    <Typography fontSize={12} color="text.secondary">
      {translate('VenueDates.QuizPanel.custom-avg-capacity-panel')}
    </Typography>
  </Stack>
);
